/**
 * Public store listings of the production AllDrivers app (ALL-22/ALL-23).
 *
 * Single frontend source for the store links. The identifiers match the
 * production app config: Android package and iOS bundle `pt.alldrivers`
 * (`app.config.js`, production variant has no suffix) and App Store Connect
 * app id `6764850597` (`eas.json` submit.production.ios.ascAppId). The backend
 * mirrors these in `settings.ANDROID_APP_STORE_URL` / `IOS_APP_STORE_URL` for
 * transactional email.
 */
import { isReferralCodeShape, normalizeReferralCode } from '@/src/components/referrals/offerCatalogue';

export const ANDROID_PACKAGE_ID = 'pt.alldrivers';
/**
 * URL scheme registered by that production binary (`app.config.js`: the
 * production variant uses `alldrivers`; dev/preview builds use
 * `alldrivers-dev` / `alldrivers-preview` but are never what the stores install).
 */
export const PRODUCTION_APP_SCHEME = 'alldrivers';
export const IOS_APP_STORE_ID = '6764850597';

export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID}`;
export const APP_STORE_URL = `https://apps.apple.com/pt/app/alldrivers/id${IOS_APP_STORE_ID}`;

/** Key the Play install referrer carries the referral code under (`ref=CODE`). */
export const INSTALL_REFERRER_CODE_KEY = 'ref';
/** Key for the store-link click time (epoch seconds), so the D-057 window starts at the click. */
export const INSTALL_REFERRER_TIME_KEY = 't';

/**
 * Play Store link that hands the referral code to the installed app through the
 * Play Install Referrer (`&referrer=ref%3DCODE%26t%3D<click epoch s>`). The Play
 * Store keeps a referrer for ~90 days, so the click time travels with it and
 * the app counts the D-057 30-day window from the click, not from first launch.
 * A missing or malformed code yields the plain listing.
 */
export function playStoreUrl(referralCode?: string | null, clickedAt: Date = new Date()): string {
  const code = normalizeReferralCode(referralCode || '');
  if (!isReferralCodeShape(code)) return PLAY_STORE_URL;
  const seconds = Math.floor(clickedAt.getTime() / 1000);
  const referrer = `${INSTALL_REFERRER_CODE_KEY}=${code}&${INSTALL_REFERRER_TIME_KEY}=${seconds}`;
  return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
}

/**
 * Custom-scheme link that reopens the installed app on the same referral (the
 * iOS fallback: universal links do not open the app from a page on the same
 * domain). It always targets the binary the store buttons next to it install —
 * the production app — never the scheme of the web build serving the page
 * (dev.alldrivers.pt is a preview web build).
 */
export function openInAppUrl(referralCode: string) {
  return `${PRODUCTION_APP_SCHEME}://register/choice?ref=${encodeURIComponent(normalizeReferralCode(referralCode))}`;
}
